import React from 'react';
import { Box, styled } from '@mui/material';
import Color from 'color';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CurrencyAmount } from '@cowbell/ui/core';
import type { RatingDetails } from '../../../api/types/rater-x-preset';
import { Show } from '../../../../../../../components/Show';
import SimpleTooltip from '../../../../../../../components/SimpleTooltip';
import { useLocale } from '../../../providers/LocaleProvider';
import { deriveCurrencyFormat } from '../../../../../../../i18n/currencies';
import type { Languages } from '../../../../../../../i18n/i18n.language-config';
import DeltaPricing from '../../../../../policies/prime-x/endorsements/components/coverages/DeltaPricing';
import {
  UW_PANEL_CONTENT_END,
  UW_PANEL_TOTALS_FONT_SIZE,
} from '../components/uwTileSpacing';
import { CS_PROPOSED_TEXT } from '../draft/components/stagedStyles';

/**
 * Renders `amount` via the caller-supplied `currency` (the policy's ISO code in the
 * MTE/coverage-endorsements flow, or the quote's own ISO code in plain quote underwriting —
 * see `getUnderwriteQuoteCurrency`), falling back to the locale-derived format only if that's
 * genuinely unavailable. Every money render in this file goes through this so a single card
 * never mixes a stamped currency symbol with a locale-guessed one.
 */
const formatAmount = (
  amount: number | null | undefined,
  currency: string | null | undefined,
  language: Languages
) =>
  currency != null ? (
    <CurrencyAmount amount={amount} currency={currency} />
  ) : (
    deriveCurrencyFormat(language, amount)
  );

/**
 * One money cell, in priority order: an endorsement delta (MTE, which never
 * stages), a staged draft as committed -> draft, or the committed figure.
 */
const renderAmount = ({
  delta,
  committed,
  draft,
  currency,
  language,
}: RenderAmountArgs) => {
  if (delta) {
    return <DeltaPricing price={delta.amount} currency={currency} />;
  }

  if (draft != null && draft !== committed) {
    return (
      <DraftAmount
        committed={committed}
        draft={draft}
        currency={currency}
        language={language}
      />
    );
  }

  return formatAmount(committed, currency, language);
};

type RenderAmountArgs = {
  readonly delta: { readonly amount: number } | null | undefined;
  readonly committed: number | null | undefined;
  readonly draft: number | null | undefined;
  readonly currency: string | null | undefined;
  readonly language: Languages;
};

/**
 * Committed struck through, draft beside it. Stacked so a narrow card never
 * clips the second figure.
 */
const DraftAmount = ({
  committed,
  draft,
  currency,
  language,
}: DraftAmountProps) => (
  // Inline with an arrow, not stacked. The footer is a right-aligned line with
  // room to its left, so it reads as a sentence — `$13,282.67 -> $12,696.61`.
  // The committed figure is not struck here: nothing is replacing it in place,
  // it is one end of a range.
  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
    <Box component="span">{formatAmount(committed, currency, language)}</Box>
    <Box component="span" color="text.disabled">
      {'\u2192'}
    </Box>
    <Box component="span" color={CS_PROPOSED_TEXT} fontWeight={700}>
      {formatAmount(draft, currency, language)}
    </Box>
  </Box>
);

type DraftAmountProps = {
  readonly committed: number | null | undefined;
  readonly draft: number;
  readonly currency: string | null | undefined;
  readonly language: Languages;
};

/** Running Aggregate and Model Premium sit a step under Category Premium. */
export const SUPPORTING_LINE_FONT_SIZE = `calc(${UW_PANEL_TOTALS_FONT_SIZE} - 0.1rem)`;

/** Category Premium is the line that matters; the rest support it. */
export const CATEGORY_LINE_WEIGHT = 700;

/** What the figure is: quieter than the figure itself. */
export const PremiumLabel = ({
  children,
  fontWeight = 400,
}: Readonly<{ children: React.ReactNode; fontWeight?: number }>) => (
  <Box component="span" color="text.secondary" fontWeight={fontWeight}>
    {children}
  </Box>
);

/** The figure. */
export const PremiumValue = ({
  children,
  fontWeight = 600,
}: Readonly<{ children: React.ReactNode; fontWeight?: number }>) => (
  <Box component="span" color="text.primary" fontWeight={fontWeight}>
    {children}
  </Box>
);

const SectionRatingDetails = ({
  ratingDetails,
  draftPremium,
  draftAggregatePremium,
  expectedRatingDetails,
  displayModelPremium,
  currency,
}: SectionRatingDetailsProps) => {
  const locale = useLocale();

  return (
    <PricingContainer>
      <Box
        mb={0.25}
        display="flex"
        justifyContent="end"
        alignItems="center"
        gap="0.25rem"
      >
        <PricingWrapper
          ratingDetails={ratingDetails}
          currency={currency}
          type="category"
        >
          <PremiumLabel fontWeight={CATEGORY_LINE_WEIGHT}>
            Category Premium: {ratingDetails.premiumDelta && 'Delta'}
          </PremiumLabel>
          <PremiumValue fontWeight={CATEGORY_LINE_WEIGHT}>
            {renderAmount({
              delta: ratingDetails.premiumDelta,
              committed: ratingDetails.premium,
              draft: draftPremium,
              currency,
              language: locale.language,
            })}
          </PremiumValue>
        </PricingWrapper>
      </Box>

      <Show when={ratingDetails.aggregatePremium !== ratingDetails.premium}>
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          gap="0.25rem"
          fontSize={SUPPORTING_LINE_FONT_SIZE}
        >
          <PricingWrapper
            ratingDetails={ratingDetails}
            currency={currency}
            type="aggregate"
          >
            <PremiumLabel>
              Running Aggregate:{' '}
              {ratingDetails.aggregatePremiumDelta && 'Delta'}
            </PremiumLabel>
            <PremiumValue>
              {renderAmount({
                delta: ratingDetails.aggregatePremiumDelta,
                committed: ratingDetails.aggregatePremium,
                draft: draftAggregatePremium,
                currency,
                language: locale.language,
              })}
            </PremiumValue>
          </PricingWrapper>
        </Box>
      </Show>

      <Show when={expectedRatingDetails != null && displayModelPremium}>
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          gap="0.25rem"
          mt={0.5}
          fontSize={SUPPORTING_LINE_FONT_SIZE}
        >
          <PricingWrapper
            ratingDetails={ratingDetails}
            currency={currency}
            type="aggregate"
          >
            <PremiumLabel>Model Premium:</PremiumLabel>
            <PremiumValue>
              {formatAmount(
                expectedRatingDetails?.aggregatePremium,
                currency,
                locale.language
              )}
            </PremiumValue>
          </PricingWrapper>
        </Box>
      </Show>
    </PricingContainer>
  );
};

export default SectionRatingDetails;

type SectionRatingDetailsProps = {
  ratingDetails: RatingDetails;
  /**
   * Subtotals for the staged draft, when one has priced. Omitted by the MTE
   * flow and whenever nothing is staged, so this file behaves as it always
   * has unless a draft is on screen.
   */
  readonly draftPremium?: number | null;
  readonly draftAggregatePremium?: number | null;
  expectedRatingDetails?: RatingDetails;
  displayModelPremium?: boolean;
  /**
   * ISO 4217 code for the policy/quote this is rendered for — the policy's currency in the
   * MTE/coverage-endorsements flow, the quote's own currency otherwise (see
   * `getUnderwriteQuoteCurrency`). `RatingDetails` itself (fir-rater's RaterXPreset family)
   * carries no currency field of its own, so this must always be threaded in by the caller.
   */
  readonly currency?: string | null;
};

const Scaled = (props: SectionRatingDetailsProps) => {
  const locale = useLocale();
  let scaledFrom = props.ratingDetails.minimumPremium;
  if (props.ratingDetails.balanceToMin === 0) {
    scaledFrom = props.ratingDetails.rawPremium;
  }

  return (
    <>
      {' '}
      <i>
        Scaled from {formatAmount(scaledFrom, props.currency, locale.language)}
      </i>
    </>
  );
};

const PremiumRoundingOffset = (props: SectionRatingDetailsProps) => {
  return (
    <>
      {' '}
      <i>
        Rounded by{' '}
        <Show when={(props.ratingDetails.premiumRoundingOffset ?? 0) > 0}>
          +
        </Show>
        {props.ratingDetails.premiumRoundingOffset}
      </i>
    </>
  );
};

const AggregatePremiumRoundingOffset = (props: SectionRatingDetailsProps) => {
  return (
    <>
      {' '}
      <i>
        Rounded by{' '}
        <Show
          when={(props.ratingDetails.aggregatePremiumRoundingOffset ?? 0) > 0}
        >
          +
        </Show>
        {props.ratingDetails.aggregatePremiumRoundingOffset}
      </i>
    </>
  );
};

const BalanceToMin = (props: SectionRatingDetailsProps) => {
  const locale = useLocale();
  return (
    <i>
      Min premium hit:{' '}
      {formatAmount(
        props.ratingDetails.rawPremium,
        props.currency,
        locale.language
      )}{' '}
      +{' '}
      {formatAmount(
        props.ratingDetails.balanceToMin,
        props.currency,
        locale.language
      )}
    </i>
  );
};

type WrapperProps = SectionRatingDetailsProps & {
  children: React.ReactNode;
  type: PremiumAdjustmentsType;
};

const PricingWrapper = (props: WrapperProps) => {
  const adjustmentComponents = Object.values(PREM_ADJUSTMENTS_MAP).filter(
    ({ show, type }: any) => show(props.ratingDetails) && type === props.type
  );

  if (!adjustmentComponents.length) {
    return <>{props.children}</>;
  }

  return (
    <SimpleTooltip
      title={
        <Box margin="0 auto" px={1.5} component="ul">
          {adjustmentComponents.map(({ Component }: any, index: any) => {
            return (
              <Box component="li" key={index} py={0}>
                <Component
                  ratingDetails={props.ratingDetails}
                  currency={props.currency}
                />
              </Box>
            );
          })}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap="0.25rem">
        <Box>
          <InfoOutlinedIcon sx={{ pointerEvents: 'none' }} />
        </Box>
        {props.children}
      </Box>
    </SimpleTooltip>
  );
};

export const PricingContainer = styled(Box)(({ theme }) => {
  return {
    backgroundColor: Color(theme.palette.background.default)
      .lighten(theme.palette.mode === 'dark' ? 0.5 : 0.05)
      .toString(),
    textAlign: 'right',
    width: '100%',
    padding: `0.5rem ${UW_PANEL_CONTENT_END} 0.5rem 1rem`,
    fontSize: UW_PANEL_TOTALS_FONT_SIZE,
    borderRadius: '0 0 4px 4px',
  };
});

const PREM_ADJUSTMENTS_MAP: PremiumAdjustments = {
  balanceToMin: {
    show: (details) => details.balanceToMin !== 0,
    Component: BalanceToMin,
    type: 'category',
  },
  scaled: {
    show: (details) => Boolean(details.scaled),
    Component: Scaled,
    type: 'category',
  },
  premiumRoundingOffset: {
    show: (details) => Boolean(details.premiumRoundingOffset),
    Component: PremiumRoundingOffset,
    type: 'category',
  },
  aggregateRoundingOffset: {
    show: (details) => Boolean(details.aggregatePremiumRoundingOffset),
    Component: AggregatePremiumRoundingOffset,
    type: 'aggregate',
  },
};

type PremiumAdjustments = {
  [key: string]: {
    show: (details: RatingDetails) => boolean;
    Component: React.ComponentType<SectionRatingDetailsProps>;
    type: PremiumAdjustmentsType;
  };
};

type PremiumAdjustmentsType = 'aggregate' | 'category';
